/*
 * Runtime DOM translator — English display mode.
 *
 * Japanese is the canonical source rendered by React. When the user selects EN
 * this module walks the rendered DOM, sends the Japanese strings it finds to the
 * backend translation service (local LLM) via `translationEngine`, and swaps the
 * text in place. Nothing is written back into application state, so switching to
 * 日本語 restores the exact original text instantly.
 *
 * Guarantees
 *   · every replaced text node / attribute keeps its original Japanese value and
 *     is restored synchronously on stop()
 *   · a response that arrives after the language changed (epoch mismatch) or
 *     after the node left the DOM / changed is discarded
 *   · untranslatable strings (identifiers, numbers, paths, already-English) are
 *     never sent
 *   · no hardcoded Japanese→English content dictionary is involved
 */
import { translate, getCached, currentEpoch, shouldSkip, localTranslate, isRejected } from './translationEngine';
import { recordMissing } from './MissingKeys';

const ATTR_LIST = ['placeholder', 'title', 'aria-label', 'alt', 'data-tooltip'];
const NO_TR_SEL = '[data-no-translate],[translate="no"],.notranslate';
const JP_RE = /[\u3040-\u30ff\u4e00-\u9fff]/;
const SKIP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'CODE', 'PRE', 'NOSCRIPT', 'IFRAME', 'CANVAS',
  'SVG', 'PATH', 'RECT', 'CIRCLE',
]);

const textNodes = new Set();   // nodes we replaced (for restore)
const attrNodes = new Set();   // elements whose attributes we replaced
const heldEls = new Set();     // T1: elements marked「英訳保留」(admins only)

// T1 — 英訳が検証で落ちた箇所に、管理者だけに見える控えめな印を出す。
let holdMarker = false;
export function setTranslationHoldMarker(on) {
  holdMarker = !!on;
  if (!holdMarker) clearHolds();
}

function clearHolds() {
  heldEls.forEach((el) => {
    try {
      delete el.dataset.i18nHold;
      if (el.dataset.i18nHoldTitle) {
        el.removeAttribute('title');
        delete el.dataset.i18nHoldTitle;
        delete el.dataset.i18nDonetitle;
      }
    } catch { /* noop */ }
  });
  heldEls.clear();
}

function markHold(el) {
  if (!holdMarker || !el || el.nodeType !== 1) return;
  if (el.dataset.i18nHold) return;
  try {
    el.dataset.i18nHold = '1';
    if (!el.getAttribute('title')) {
      el.setAttribute('title', '英訳保留 / Translation on hold');
      el.dataset.i18nHoldTitle = '1';
      el.dataset.i18nDonetitle = '英訳保留 / Translation on hold';  // 翻訳対象から外す
    }
    heldEls.add(el);
  } catch { /* noop */ }
}

function applyText(node, english) {
  const original = node.__i18n_original;
  if (original === undefined) return;
  const lead = original.match(/^\s*/)[0];
  const tail = original.match(/\s*$/)[0];
  const next = lead + english + tail;
  node.__i18n_last = next;
  node.nodeValue = next;
  textNodes.add(node);
}

let deferred = [];        // off-screen nodes translated in a second pass
let phase = 1;

function isFarOffScreen(el) {
  if (phase !== 1 || !el || !el.getBoundingClientRect) return false;
  try {
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return true;         // hidden panel/tab
    return r.top > window.innerHeight * 1.3;
  } catch { return false; }
}

function translateTextNode(node) {
  if (!active) return;
  if (!node || node.nodeType !== 3) return;
  const parent = node.parentElement;
  if (!parent || SKIP_TAGS.has(parent.tagName) || parent.isContentEditable) return;
  if (isFarOffScreen(parent)) { deferred.push(node); return; }
  const raw = node.nodeValue;
  if (!raw || !JP_RE.test(raw)) return;
  const trimmed = raw.trim();
  if (!trimmed) return;
  if (node.__i18n_last === raw) return;          // our own output

  // T2 — 業務データの目印（data-no-translate / translate="no" / .notranslate）が
  // 付いた範囲はモデルに送らない。登録済みの英語名・定型文だけその場で差し替える。
  const guard = parent.closest && parent.closest(NO_TR_SEL);
  // data-no-translate="strict" は英語名の差し替えすら行わない（原文をそのまま出す。
  // BizName が英語表記を別行で見せるので、同じ語が二重に出るのを防ぐ）。
  if (guard && guard.getAttribute('data-no-translate') === 'strict') return;
  const guarded = !!guard;
  const local = localTranslate(trimmed);
  if (local) {
    node.__i18n_original = raw;
    applyText(node, local);
    return;
  }
  if (guarded) return;
  if (shouldSkip(trimmed)) return;               // identifiers / numbers / paths

  node.__i18n_original = raw;
  const cached = getCached(trimmed);
  if (cached) { applyText(node, cached); return; }

  const epochAtRequest = currentEpoch();
  translate(trimmed, 'ui_text').then((english) => {
    if (!english) {
      recordMissing(trimmed, parent.tagName);
      // 転送障害（モデルに繋がらない等）では印を出さない。検証で捨てた訳だけ。
      if (active && epochAtRequest === currentEpoch() && node.isConnected
          && isRejected(trimmed)) markHold(parent);
      return;
    }
    if (!active || epochAtRequest !== currentEpoch()) return;   // language changed
    if (!node.isConnected) return;                              // node gone
    if (node.nodeValue !== node.__i18n_original) return;        // React re-rendered
    applyText(node, english);
  });
}

function translateAttrs(el) {
  if (!active) return;
  if (!el || el.nodeType !== 1) return;
  const guard = el.closest && el.closest(NO_TR_SEL);
  if (guard && guard.getAttribute('data-no-translate') === 'strict') return;
  const guarded = !!guard;
  for (const attr of ATTR_LIST) {
    const v = el.getAttribute(attr);
    if (!v || !JP_RE.test(v)) continue;
    const marker = 'i18nDone' + attr.replace(/[^a-z]/gi, '');
    if (el.dataset && el.dataset[marker] === v) continue;
    const trimmed = v.trim();
    if (!el.__i18n_attrs) el.__i18n_attrs = {};

    const setter = (english) => {
      el.setAttribute(attr, english);
      if (el.dataset) el.dataset[marker] = english;
      attrNodes.add(el);
    };
    const local = localTranslate(trimmed);
    if (local) {
      if (el.__i18n_attrs[attr] === undefined) el.__i18n_attrs[attr] = v;
      setter(local);
      continue;
    }
    if (guarded) continue;
    if (shouldSkip(trimmed)) continue;
    if (el.__i18n_attrs[attr] === undefined) el.__i18n_attrs[attr] = v;
    const cached = getCached(trimmed);
    if (cached) { setter(cached); continue; }
    const epochAtRequest = currentEpoch();
    translate(trimmed, 'label').then((english) => {
      if (!english) { recordMissing(trimmed, `${el.tagName}[${attr}]`); return; }
      if (!active || epochAtRequest !== currentEpoch()) return;
      if (!el.isConnected) return;
      if (el.getAttribute(attr) !== el.__i18n_attrs[attr]) return;
      setter(english);
    });
  }
}

function walk(root) {
  if (!root) return;
  if (root.nodeType === 3) { translateTextNode(root); return; }
  if (root.nodeType !== 1) return;
  if (SKIP_TAGS.has(root.tagName)) return;
  translateAttrs(root);
  for (let n = root.firstChild; n; n = n.nextSibling) walk(n);
}

let observer = null;
let active = false;

function restoreAll() {
  textNodes.forEach((node) => {
    try {
      if (node.__i18n_original !== undefined && node.isConnected) {
        node.nodeValue = node.__i18n_original;
      }
      delete node.__i18n_last;
    } catch { /* noop */ }
  });
  textNodes.clear();
  attrNodes.forEach((el) => {
    try {
      Object.entries(el.__i18n_attrs || {}).forEach(([attr, original]) => {
        if (el.isConnected) el.setAttribute(attr, original);
        const marker = 'i18nDone' + attr.replace(/[^a-z]/gi, '');
        if (el.dataset) delete el.dataset[marker];
      });
      delete el.__i18n_attrs;
    } catch { /* noop */ }
  });
  attrNodes.clear();
  clearHolds();
}

function stop() {
  if (observer) { observer.disconnect(); observer = null; }
  active = false;
  phase = 1;
  deferred = [];
  restoreAll();     // canonical Japanese is shown again immediately
}

let pendingRoots = [];
let rafHandle = 0;
function flush() {
  const roots = pendingRoots; pendingRoots = []; rafHandle = 0;
  const seen = new WeakSet();
  for (const r of roots) {
    if (!r || seen.has(r)) continue;
    seen.add(r);
    walk(r);
  }
}
function schedule(root) {
  if (!root) return;
  pendingRoots.push(root);
  if (!rafHandle) rafHandle = requestAnimationFrame(flush);
}

function start() {
  if (active) return;
  active = true;
  // 見えている見出し・ナビ・カードを先に翻訳し、画面外や折りたたみは後回し。
  phase = 1;
  deferred = [];
  walk(document.body);
  setTimeout(() => {
    if (!active) return;
    phase = 2;
    const rest = deferred;
    deferred = [];
    rest.forEach((n) => { if (n.isConnected) translateTextNode(n); });
    walk(document.body);
  }, 1200);
  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'characterData') {
        translateTextNode(m.target);
      } else if (m.type === 'attributes') {
        if (m.target && m.target.nodeType === 1) translateAttrs(m.target);
      } else if (m.type === 'childList') {
        m.addedNodes.forEach((n) => schedule(n));
      }
    }
  });
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ATTR_LIST,
  });
}

export function syncLangTranslator(lang) {
  try {
    if (lang === 'en') start(); else stop();
  } catch { /* noop */ }
}

/** Manual re-pass (e.g. after a retry from the translation status pill). */
export function retranslateNow() {
  if (active) walk(document.body);
}

/**
 * Collect the Japanese strings currently rendered on screen — used to prewarm
 * English while the user is still in Japanese mode. Read-only: nothing in the
 * DOM is modified.
 */
export function collectJapaneseStrings(limit = 60) {
  const out = [];
  const seen = new Set();
  const push = (raw) => {
    const s = String(raw || '').trim();
    if (!s || !JP_RE.test(s) || seen.has(s) || s.length > 300) return;
    seen.add(s);
    out.push(s);
  };
  const visit = (node) => {
    if (out.length >= limit || !node) return;
    if (node.nodeType === 3) { push(node.nodeValue); return; }
    if (node.nodeType !== 1) return;
    if (SKIP_TAGS.has(node.tagName)) return;
    if (node.hasAttribute && (node.hasAttribute('data-no-translate')
        || node.getAttribute('translate') === 'no'
        || (node.classList && node.classList.contains('notranslate')))) return;
    for (const attr of ATTR_LIST) {
      const v = node.getAttribute && node.getAttribute(attr);
      if (v) push(v);
    }
    for (let n = node.firstChild; n; n = n.nextSibling) visit(n);
  };
  visit(document.body);
  return out.slice(0, limit);
}
